import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { errorMessage, useCompleteSlackInstall } from '../../../../api/integrations.js';
import { LoadingArea, StartPageLink, TextPage } from '../../../../components/PlaceholderPage.js';

/**
 * `/integrations/slack/callback`: where Slack sends the browser back after
 * "Add to Slack" (`features/integrations`). It hands Slack's code and the
 * state to the api, which connects the channel, then opens the website's
 * Integrations. Turning Slack down comes back with `error` instead.
 */
const searchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute('/_app/integrations/slack/callback')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: SlackCallbackPage,
});

const TITLE = "Slack wasn't connected";

function SlackCallbackPage() {
  const { code, state, error } = Route.useSearch();
  const navigate = useNavigate();
  const complete = useCompleteSlackInstall();
  // Slack's code works once: never send it twice, e.g. when React mounts twice in development.
  const sent = useRef(false);

  const canComplete = error === undefined && code !== undefined && state !== undefined;

  useEffect(() => {
    if (!canComplete || sent.current) return;
    sent.current = true;
    complete.mutate(
      { code, state },
      {
        onSuccess: ({ websiteId }) =>
          void navigate({
            to: '/websites/$websiteId/integrations',
            params: { websiteId },
            search: { integration: 'slack' },
            replace: true,
          }),
      },
    );
  }, [canComplete, code, state, complete, navigate]);

  if (error !== undefined) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        {error === 'access_denied'
          ? "Groundly wasn't allowed to post in Slack. Add Slack again from the website's Integrations when you're ready."
          : "Slack stopped before a channel was chosen. Add Slack again from the website's Integrations."}
      </TextPage>
    );
  }
  if (!canComplete) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        This page is only for coming back from Slack. Add Slack from the website&apos;s
        Integrations.
      </TextPage>
    );
  }
  if (complete.isError) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        {errorMessage(complete.error, "Groundly couldn't reach the server. Add Slack again.")}
      </TextPage>
    );
  }
  return <LoadingArea />;
}
